import { Injectable } from '@angular/core';
import { sessionReplayPlugin, SessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

import { BrowserClient } from '../ampli';

/**
 * Fraction of production sessions recorded, as a decimal between 0 and 1.
 *
 * The licence affords 50k replays a month, and this is a deliberately cautious opening position
 * rather than a considered steady state: start well inside the allotment, watch what it actually
 * consumes, raise it once this first phase has told us something. Production runs ~8-9.5k
 * sessions on a weekday and ~2.5k at the weekend, so ~200k/month; 0.05 puts us at roughly 10k
 * recordings, a fifth of the allowance. For comparison the Datadog RUM replay beside it runs at
 * 20% (`sessionReplaySampleRate: 20` in datadog-rum-config.service.ts).
 *
 * Sampling is per *session*, not per user: the SDK hashes the session id, which is the timestamp
 * the session started, so a user is recorded in roughly this fraction of their sessions rather
 * than being permanently in or out of the sample. Sessions roll over after 30 minutes idle.
 *
 * Note that Amplitude's remote configuration wins over this value: a sample rate set on the
 * project in the Amplitude console replaces it entirely (see the remote/local join in
 * @amplitude/session-replay-browser's joined-config).
 */
export const SESSION_REPLAY_SAMPLE_RATE = 0.05;

/**
 * Masking level applied to everything we record.
 *
 * `conservative` is the strictest level the SDK offers: every input *and* all page text is
 * replaced with asterisks before it leaves the browser. Anything less is unsafe here — Amplitude
 * defaults to `medium`, which masks form fields but captures page text verbatim, and nearly every
 * Pure1 screen renders customer org names, appliance names, serial numbers and user emails as
 * plain text. This mirrors the `defaultPrivacyLevel: 'mask'` we already run on Datadog RUM.
 *
 * Caveats worth knowing before relaxing this:
 * - A `defaultMaskLevel` set remotely on the Amplitude project overrides this local value.
 * - Masking covers text and inputs, not HTML attributes. The plugin does not forward the
 *   standalone SDK's `maskAttributes`, so anything written into `title`, `aria-label`, `href`
 *   and friends is recorded verbatim. {@link SESSION_REPLAY_BLOCK_SELECTORS} is the lever for
 *   the cases where that matters.
 */
export const SESSION_REPLAY_MASK_LEVEL = 'conservative';

/**
 * Elements dropped from the recording entirely, rather than merely masked.
 *
 * A blocked element is serialised as a placeholder: rrweb keeps its `class` and measured size and
 * throws away every other attribute and all of its children. That is the only way to keep an
 * attribute out of a replay, since the plugin cannot forward `maskAttributes`.
 *
 * The cross-app links in the left navigation (Support Center, Identity Center) hand the signed-in
 * user's email to the target app as a `login_hint` query parameter, so their `href` carries a real
 * address — the impersonated customer's address, when someone is impersonating. Conservative
 * masking does not touch it because it is an attribute, so the nodes go instead; they replay as
 * correctly-sized blanks.
 */
export const SESSION_REPLAY_BLOCK_SELECTORS = ['a[href*="login_hint"]'];

/** Options handed to the Amplitude session replay plugin. */
export interface AmplitudeSessionReplayOptions {
    sampleRate: number;
    privacyConfig: {
        defaultMaskLevel: typeof SESSION_REPLAY_MASK_LEVEL;
        blockSelector: string[];
    };
}

/**
 * Installs Amplitude's Session Replay plugin onto the Amplitude browser client.
 *
 * Production only. Amplitude replay is distinct from the Datadog RUM replay this app already
 * records; it is what lets us jump from a funnel drop-off in Amplitude straight into the
 * recording. Dev/staging may be switched on temporarily to verify recording works, but must not
 * ship enabled.
 */
@Injectable({ providedIn: 'root' })
export class AmplitudeSessionReplayService {
    /** The plugin options. Split out so tests can assert on them without constructing a plugin. */
    buildOptions(): AmplitudeSessionReplayOptions {
        return {
            sampleRate: SESSION_REPLAY_SAMPLE_RATE,
            privacyConfig: {
                defaultMaskLevel: SESSION_REPLAY_MASK_LEVEL,
                blockSelector: SESSION_REPLAY_BLOCK_SELECTORS,
            },
        };
    }

    /**
     * Wrapper for sessionReplayPlugin(), mostly so we can spy/stub it in unit tests.
     */
    createPlugin(): SessionReplayPlugin {
        return sessionReplayPlugin(this.buildOptions());
    }

    /**
     * Adds the replay plugin to an already-initialised Amplitude client, in production only.
     *
     * Failures are swallowed: session replay is strictly additive, and a plugin that cannot start
     * must never take event tracking down with it.
     *
     * @param client the Amplitude client returned by ampli.load()
     * @param isProduction whether the app is running on a Pure1 production host
     */
    async install(client: BrowserClient, isProduction: boolean): Promise<void> {
        if (!isProduction) {
            return;
        }
        try {
            await client.add(this.createPlugin()).promise;
        } catch (e) {
            // Intentionally ignored - see method docs.
        }
    }
}
